/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, March 2020
 */
let store: WeakMap<object, WeakMap<Function, Function>>;

/**
 * Get the bound method of a super class.
 *
 * @param instance The instance to get the super.method from.
 * @param method The super method.
 * @returns A `method` bound to `instance`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getBoundSuperMethod(instance: any, method: Function): Function {
    if (typeof WeakMap === 'undefined') {
        throw new Error(
            `Using @autobind on ${method.name}() requires WeakMap support due to its use of super.${method.name}()
        See https://github.com/jayphelps/core-decorators.js/issues/20`
        );
    }

    if (!store) {
        store = new WeakMap();
    }

    if (!store.has(instance)) {
        store.set(instance, new WeakMap());
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const superStore = store.get(instance)!;

    if (!superStore.has(method)) {
        superStore.set(method, method.bind(instance));
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return superStore.get(method)!;
}

/**
 * Automatically binds a method to it's instance.
 */
export function autobind(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    target: any,
    key: string | symbol,
    { value: fn, configurable, enumerable }: PropertyDescriptor
): PropertyDescriptor {
    // istanbul ignore next
    if (typeof fn !== 'function') {
        throw new SyntaxError(`@autobind can only be used on functions, not: ${fn}`);
    }

    const { constructor } = target;

    return {
        configurable,
        enumerable,

        get(): Function {
            // Class.prototype.key lookup
            // Someone accesses the property directly on the prototype on which it is
            // actually defined on, i.e. Class.prototype.hasOwnProperty(key)
            if (this === target) {
                return fn;
            }

            // Class.prototype.key lookup
            // Someone accesses the property directly on a prototype but it was found
            // up the chain, not defined directly on it
            // i.e. Class.prototype.hasOwnProperty(key) == false && key in Class.prototype
            if (
                this.constructor !== constructor &&
                Object.getPrototypeOf(this).constructor === constructor
            ) {
                return fn;
            }

            // Autobound method calling super.sameMethod() which is also autobound and so on.
            if (this.constructor !== constructor && key in this.constructor.prototype) {
                return getBoundSuperMethod(this, fn);
            }

            const bound = fn.bind(this);

            Object.defineProperty(this, key, {
                configurable: true,
                writable: true,
                // NOT enumerable when it's a bound method
                enumerable: false,
                value: bound
            });

            return bound;
        }
    };
}



// WEBPACK FOOTER //
// ./src/packages/core/lib/reflection/autobind.ts